import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { Button as Button95, Divider, Hourglass, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { Link, RelativePathString, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Meta } from '@/components/Meta';
import { Image } from 'expo-image';
import { PasswordShowHideButton } from '@/components/PasswordShowHideButton';
import { setLoggedIn } from '@/state/user';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  bold: {
    fontWeight: 'bold'
  },
});

interface SigninParams {
  simpleLoginStateUpdate?: boolean,
}

export default function Signin({
  simpleLoginStateUpdate = false
} : SigninParams) {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const emailInput = useRef<RNTextInput>(null);
  const passwordInput = useRef<RNTextInput>(null);
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const isMainLogicalHost = useSelector((state: RootState) => state.user.isMainLogicalHost);
  const domain = useSelector((state: RootState) => state.user.domain);
  const [passwordShown, setPasswordShown] = useState(false);

  const { register, control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
      password: '',
    }
  });

  const onSubmit = useCallback(async (data: any) => {
    try {
      setError('');
      showSpinner();

      const response = await AppUtilities.fetchUnauthorized(
        "Login.onSubmit",
        "/rabbl/api/signin",
        "POST",
        {
          email: data.email,
          password: data.password,
          domain: isMainLogicalHost ? null : domain,
        }
      );

      if (simpleLoginStateUpdate) {
        const loggedIn: any = ((response as any).body as any).loggedIn;
        if (loggedIn) {
          AppUtilities.login((response as any).body);
          dispatch(setLoggedIn(true));
        }
      } else {
        if (isMainLogicalHost) {
          AppUtils.Globals.nextRootRedirect = "/rabbls" as RelativePathString;
        } else {
          const membership: CommunityMembership | null = ((response as any).body as any).community_membership;
          if (membership) {
            AppUtilities.info("community login has membership");
            AppUtils.Globals.nextRootRedirect = AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString;
          } else {
            AppUtilities.info("community login no membership");
            AppUtils.Globals.nextRootRedirect = AppConsts.SUBDOMAIN_HOMEPAGE_LOGGED_IN_UNJOINED as RelativePathString;
          }
        }

        AppUtilities.login((response as any).body);
        dispatch(setData(response));
      }
    } catch (e : any) {
      let shouldReportException = true;
      if (e.status && e.status > 0) {
        if (e.status == 403 || e.status == 404) {
          shouldReportException = false;
        }
      }
      //AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      setError(AppUtilities.getErrors(e));
      if (shouldReportException) {
        AppUtilities.handleException(e, "Login.onSubmit");
      }
    } finally {
      hideSpinner();
    }
  }, [isMainLogicalHost, domain]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (emailInput && emailInput.current) {
        emailInput.current?.focus();
      }
    }
  }, [emailInput]);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Login"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <AppText>Log into an existing account</AppText>
          <Controller
            control={control}
            name="email"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={emailInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Email"
                  style={styles.formElement}
                  returnKeyType="next"
                  onBlur={onBlur}
                  autoComplete="email"
                  keyboardType="email-address"
                  textContentType="emailAddress"
                  onSubmitEditing={() => passwordInput.current?.focus()}
                  autoCapitalize="none"
                />
                {AppUtils.SHOW_PASSWORD_BLANK}
              </View>
            )}
          />
          {errors.email && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ Email is required. ^</AppText>}

          <Controller
            control={control}
            name="password"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={AppConstants.STYLES.passwordInputRow}>
                <TextInput
                  ref={passwordInput}
                  value={value}
                  onChangeText={onChange}
                  placeholder="Password"
                  style={styles.formElement}
                  returnKeyType="go"
                  onBlur={onBlur}
                  autoComplete="password"
                  textContentType="password"
                  onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                  secureTextEntry={passwordShown ? false : true}
                />
                <PasswordShowHideButton passwordShown={passwordShown} setPasswordShown={setPasswordShown} />
              </View>
            )}
          />
          {errors.password && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ Password is required. ^</AppText>}

          <View style={styles.inputWrapper}>
            <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)}><AppText style={AppConstants.STYLES.fontBold}>Login</AppText></Button95>
          </View>

          { (error && error.length > 0) ?
                (
                  <>
                    <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>{error}</AppText>
                  </>
                )
              :
                null
          }

          <Link href="/forgotpassword" asChild className={"nontext"}><Button95>Forgot password?</Button95></Link>

          <Divider />

          <AppText>Don't have an account?</AppText>

          <Link href="/signup" asChild className={"nontext"}><Button95>Create new account</Button95></Link>

        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
